// src/pages/automations/EditAutomation.tsx

import React, { useState, useRef, useEffect } from 'react';
import HierarchyGraphEdit from './HierarchyGraphEdit';
import { useNavigate, useParams } from 'react-router-dom';
import { FiChevronLeft, FiEdit2, FiSave } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import {
  updateAutomationData,
  saveAutomation,
} from '../../features/automation/automationSlice';
import { toast, ToastContainer } from 'react-toastify';
import { transformApiAutomationToFrontend } from './automationGraphMapper';
import { getAuthContext } from '../../utils/jwt';
import { updateAutomationDraft, publishAutomation } from './automationApi';
// @ts-ignore
import 'react-toastify/dist/ReactToastify.css';
import { BASE_URL } from "../../utils/constants";

const FiChevronLeftComponent = FiChevronLeft as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiEdit2Component = FiEdit2 as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiSaveComponent = FiSave as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

export default function EditAutomation() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();

  const { automationFlowName, trigger, conditions } = useSelector(
    (state: RootState) => state.automation
  );

  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(automationFlowName || 'untitled');
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const hierarchyGraphRef = useRef<React.ElementRef<typeof HierarchyGraphEdit> | null>(null);

  const [loadedAutomation, setLoadedAutomation] = useState<{
    id: string;
    name: string;
    trigger: string;
    status: string;
    nodes: any[];
    edges: any[];
    customFormUrl?: string;
    customFormFields?: any[];
    raw?: any;
  } | null>(null);

  // ------------------------------------------------------------------
  // Load automation - ALWAYS fetch from API to get full graph data
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!id) return;

    const fetchAutomation = async () => {
      setLoading(true);

      const { token, tenantId, userId } = getAuthContext();
      if (!token) {
        toast.error('Authentication required');
        navigate('/automations');
        setLoading(false);
        return;
      }



      try {

        const response = await fetch(
          `${BASE_URL}/api/v1/automations/${id}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'X-Tenant-Id': tenantId,
              'X-User-Id': userId || '',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to load automation: ${response.status}`);
        }

        const apiData = await response.json();

        const transformed = transformApiAutomationToFrontend(apiData);

        // Update Redux store
        dispatch(
          updateAutomationData({
            automationFlowName: transformed.name,
            trigger: transformed.trigger,
            conditions: transformed.conditions,
          })
        );

        // Save to Redux for future fast path
        dispatch(
          saveAutomation({
            id: transformed.id,
            name: transformed.name,
            trigger: transformed.trigger,
            status: transformed.status as 'draft' | 'published',
            nodes: transformed.nodes,
            edges: transformed.edges,
          })
        );

        setEditedName(transformed.name);
        setLoadedAutomation({
          id: transformed.id,
          name: transformed.name,
          trigger: transformed.trigger,
          status: transformed.status,
          nodes: transformed.nodes,
          edges: transformed.edges,
          raw: apiData,
        });


      } catch (err: any) {
        // console.error('❌ Failed to load automation', err);
        toast.error(err.message || 'Automation not found or failed to load', {
          position: 'top-right',
          autoClose: 3000,
          theme: 'colored',
          style: { backgroundColor: '#fef3c7', color: '#d97706' },
        });
        navigate('/automations');
      } finally {
        setLoading(false);
      }
    };

    fetchAutomation();
  }, [id, dispatch, navigate]);

  // ------------------------------------------------------------------
  // Name edit helpers
  // ------------------------------------------------------------------
  const handleSave = () => {
    if (editedName && editedName.trim() !== automationFlowName) {
      if (editedName.trim() === '') {
        toast.warn('Flow name cannot be empty!', {
          position: 'top-right',
          autoClose: 3000,
          theme: 'colored',
          style: { backgroundColor: '#fef3c7', color: '#d97706' },
        });
        return;
      }
      dispatch(updateAutomationData({ automationFlowName: editedName }));
      toast.success('Automation flow name updated successfully!');
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedName(automationFlowName || 'untitled');
    setIsEditing(false);
  };

  const isNameChanged = editedName.trim() !== automationFlowName;

  // ------------------------------------------------------------------
  // Update Draft
  // ------------------------------------------------------------------
  const handleUpdateDraft = async () => {
    if (isSaving) return;

    if (automationFlowName === 'untitled' || !automationFlowName?.trim()) {
      toast.warn('Please change the automation name from "untitled" before updating!', {
        position: 'top-right',
        autoClose: 4000,
        theme: 'colored',
        style: { backgroundColor: '#fef3c7', color: '#d97706' },
      });
      return;
    }

    if (hierarchyGraphRef.current) {
      const { nodes, edges } = hierarchyGraphRef.current;

      const hasTagNode = nodes.some(
        (node) => node.data.type === 'tags' && !node.data.tag
      );
      if (hasTagNode) {
        toast.error('Please fill all required tag fields before updating!', {
          position: 'top-right',
          autoClose: 3000,
          theme: 'colored',
          style: { backgroundColor: '#fef3c7', color: '#d97706' },
        });
        return;
      }

      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required. Please log in again.', {
          position: 'top-right',
          autoClose: 3000,
          theme: 'colored',
        });
        return;
      }

      setIsSaving(true);

      try {

        // Call the API to update automation draft with conditions
        const result = await updateAutomationDraft(
          id!,
          automationFlowName,
          trigger || '',
          nodes,
          edges,
          token,
          tenantId,
          userId,
          BASE_URL,
          conditions || []
        );

        // Update Redux store
        const simplifiedNodes = nodes.map((node) => ({
          id: node.id,
          label: node.data.label,
          type: node.data.type,
          position: node.position,
          tag: node.data.tag || '',
          selectedTags: node.data.selectedTags || [],
          template: node.data.template || '',
          variantId: node.data.variantId || '',
          variables: node.data.variables || { name: '', address: '' },
          delayType: node.data.delayType || '',
          amount: node.data.amount || 0,
          unit: node.data.unit || '',
          date: node.data.date || '',
          time: node.data.time || '',
          conditions: node.data.conditions || [],
          maxWaitAmount: node.data.maxWaitAmount || 0,
          maxWaitUnit: node.data.maxWaitUnit || '',
          buckets: node.data.buckets || [],
        }));

        const simplifiedEdges = edges.map((edge) => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
        }));

        dispatch(
          saveAutomation({
            id: result.id,
            name: automationFlowName,
            trigger: trigger || '',
            status: 'draft',
            nodes: simplifiedNodes,
            edges: simplifiedEdges,
          })
        );

        toast.success('Automation updated successfully!', {
          position: 'top-right',
          autoClose: 3000,
          theme: 'colored',
        });

        navigate('/automations');

        setTimeout(() => {
          window.location.reload();
        }, 1000);

      } catch (error: any) {
        // console.error('Error updating automation:', error);
        toast.error(error.message || 'Failed to update automation. Please try again.', {
          position: 'top-right',
          autoClose: 4000,
          theme: 'colored',
          style: { backgroundColor: '#fef2f2', color: '#dc2626' },
        });
      } finally {
        setIsSaving(false);
      }
    }
  };

  // ------------------------------------------------------------------
  // Publish
  // ------------------------------------------------------------------
  const handlePublish = async () => {
    if (isSaving) return;

    if (automationFlowName === 'untitled' || !automationFlowName?.trim()) {
      toast.warn('Please change the automation name from "untitled" before publishing!', {
        position: 'top-right',
        autoClose: 4000,
        theme: 'colored',
        style: { backgroundColor: '#fef3c7', color: '#d97706' },
      });
      return;
    }

    if (!trigger) {
      toast.warn('Please select a trigger before publishing!', {
        position: 'top-right',
        autoClose: 3000,
        theme: 'colored',
        style: { backgroundColor: '#fef3c7', color: '#d97706' },
      });
      return;
    }

    if (hierarchyGraphRef.current) {
      const { nodes, edges } = hierarchyGraphRef.current;

      const hasTagNode = nodes.some(
        (node) => node.data.type === 'tags' && !node.data.tag
      );
      if (hasTagNode) {
        toast.error('Please fill all required tag fields before publishing!', {
          position: 'top-right',
          autoClose: 3000,
          theme: 'colored',
          style: { backgroundColor: '#fef3c7', color: '#d97706' },
        });
        return;
      }

      const hasEmptyWaitUntilNode = nodes.some(
        (node) =>
          node.data.type === 'wait' &&
          (!node.data.maxWaitAmount || !node.data.maxWaitUnit)
      );
      if (hasEmptyWaitUntilNode) {
        toast.error(
          'Please fill all max wait amount and unit fields for Wait Until nodes before publishing!',
          {
            position: 'top-right',
            autoClose: 3000,
            theme: 'colored',
            style: { backgroundColor: '#fef3c7', color: '#d97706' },
          }
        );
        return;
      }

      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required. Please log in again.', {
          position: 'top-right',
          autoClose: 3000,
          theme: 'colored',
        });
        return;
      }

      setIsSaving(true);

      try {

        // Call the API to update draft first
        await updateAutomationDraft(
          id!,
          automationFlowName,
          trigger,
          nodes,
          edges,
          token,
          tenantId,
          userId,
          BASE_URL,
          conditions || []
        );

        // Then call the API to publish automation (no body required)
        const result = await publishAutomation(
          id!,
          token,
          tenantId,
          userId,
          BASE_URL
        );

        // Update Redux store
        const simplifiedNodes = nodes.map((node) => ({
          id: node.id,
          label: node.data.label,
          type: node.data.type,
          position: node.position,
          tag: node.data.tag || '',
          selectedTags: node.data.selectedTags || [],
          template: node.data.template || '',
          variantId: node.data.variantId || '',
          variables: node.data.variables || { name: '', address: '' },
          delayType: node.data.delayType || '',
          amount: node.data.amount || 0,
          unit: node.data.unit || '',
          date: node.data.date || '',
          time: node.data.time || '',
          conditions: node.data.conditions || [],
          maxWaitAmount: node.data.maxWaitAmount || 0,
          maxWaitUnit: node.data.maxWaitUnit || '',
          buckets: node.data.buckets || [],
        }));

        const simplifiedEdges = edges.map((edge) => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
        }));

        dispatch(
          saveAutomation({
            id: result.id,
            name: automationFlowName,
            trigger,
            status: 'published',
            nodes: simplifiedNodes,
            edges: simplifiedEdges,
          })
        );

        toast.success('Automation published successfully!', {
          position: 'top-right',
          autoClose: 3000,
          theme: 'colored',
        });

        navigate('/automations');

        setTimeout(() => {
          window.location.reload();
        }, 1000);

      } catch (error: any) {
        // console.error('Error publishing automation:', error);
        toast.error(error.message || 'Failed to publish automation. Please try again.', {
          position: 'top-right',
          autoClose: 4000,
          theme: 'colored',
          style: { backgroundColor: '#fef2f2', color: '#dc2626' },
        });
      } finally {
        setIsSaving(false);
      }
    }
  };

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center text-gray-500">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          <span>Loading automation...</span>
        </div>
      </div>
    );
  }

  if (!loadedAutomation) {
    return null;
  }

  return (
    <div className="p-2">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center text-[14px] text-[#00899E] mb-4 hover:underline font-semibold"
      >
        <FiChevronLeftComponent className="mr-1" /> Back to Automations
      </button>

      <div className="flex justify-between items-center flex-wrap gap-[10px]">
        <div>
          <h2 className="text-[20px] font-bold text-[#3F5265]">
            Edit Automation
          </h2>
          <p className="text-[#657584] mb-6 text-[16px]">
            Update your campaign to reach your audience
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleUpdateDraft}
            disabled={isSaving}
            className={`px-4 py-2 rounded-md border border-[#FF6B4A] text-[#FF6B4A] hover:bg-[#fff2ef] ${isSaving ? 'opacity-50 cursor-not-allowed' : ''
              }`}
          >
            {isSaving ? 'Updating...' : 'Update Draft'}
          </button>
          <button
            onClick={handlePublish}
            disabled={isSaving}
            className={`px-4 py-2 rounded-md bg-[#FF6B4A] text-white hover:bg-[#ff5833] ${isSaving ? 'opacity-50 cursor-not-allowed' : ''
              }`}
          >
            {isSaving ? 'Publishing...' : 'Publish Automation'}
          </button>
        </div>
      </div>

      <div className="bg-[#e7eef3] rounded-md border border-gray-200 shadow-sm">
        <div
          className="flex items-center justify-start border-b border-gray-200 bg-white rounded-t-md mb-4 p-4"
          style={{ boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)' }}
        >
          <div className="flex items-center">
            {isEditing ? (
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                className="text-[16px] font-semibold text-[#3F5265] mr-2 border border-gray-300 rounded-md p-1 focus:ring-2 focus:ring-teal-400"
                autoFocus
              />
            ) : (
              <span className="text-[16px] font-semibold text-[#3F5265] mr-2">
                {automationFlowName || 'untitled'}
              </span>
            )}
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#FEF3F2] text-[#F04438]">
              {loadedAutomation.status === 'published' ? 'Active' : 'Draft'}
            </span>
          </div>

          <div className="flex items-center gap-2 ml-3">
            {!isEditing && (
              <button
                onClick={() => {
                  setIsEditing(true);
                  setEditedName(automationFlowName || 'untitled');
                }}
                className="relative rounded-sm border border-teal-600 p-1 px-2 shadow-md cursor-pointer hover:bg-teal-50 group"
              >
                <FiEdit2Component size={16} className="text-teal-600" />
                <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 scale-0 group-hover:scale-100 transition-all rounded bg-gray-800 px-2 py-1 text-xs text-white whitespace-nowrap">
                  Edit
                </span>
              </button>
            )}
            {isEditing && (
              <>
                <button
                  onClick={handleSave}
                  disabled={!isNameChanged}
                  className={`relative rounded-sm p-1 px-2 shadow-md cursor-pointer group ${!isNameChanged
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-teal-600 text-white hover:bg-teal-700'
                    }`}
                >
                  <FiSaveComponent size={18} />
                  <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 scale-0 group-hover:scale-100 transition-all rounded bg-gray-800 px-2 py-1 text-xs text-white whitespace-nowrap">
                    Save
                  </span>
                </button>
                <button
                  onClick={handleCancel}
                  className="relative group rounded-sm border border-teal-600 p-1 px-2 shadow-md cursor-pointer hover:bg-teal-50"
                >
                  <FiEdit2Component size={16} className="text-teal-600" />
                  <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 scale-0 group-hover:scale-100 transition-all rounded bg-gray-800 px-2 py-1 text-xs text-white whitespace-nowrap">
                    Close Edit
                  </span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Hierarchy – receives the transformed nodes/edges */}
        <HierarchyGraphEdit
          ref={hierarchyGraphRef}
          initialNodes={loadedAutomation.nodes as any}
          initialEdges={loadedAutomation.edges}
          automation={loadedAutomation.raw}
        />
      </div>
      <ToastContainer />
    </div>
  );
}